
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, FormGroup, FormLabel, ListGroup, Row, Table } from 'react-bootstrap'
import { useDispatch } from 'react-redux'

import { useProgrammation } from '@/Hooks/UseProgrammation'
import useTranslation from '@/Hooks/UseTranslation'

import { updateTaskField } from '@/Services/ProgrammationManagerService'
import { updateTaskFieldAction } from '@/Redux/Actions/ProgrammationManagerActions'

import ContentBlock from '../../components/ContentBlock'
import InputText from '../../components/InputText'

import './Costs.scss'
import CostCreator from './CostCreator'
import CostItem from './CostItem'

const Costs = () => {

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const { itemOpened, setEditingItem } = useProgrammation()

  // const [ editingItem, setEditingItem ] = useState(false)

  const [ taskUnit, setTaskUnit ] = useState(itemOpened.unit)
  const [ currency, setCurrency ] = useState(itemOpened.currency)
  const [ taskMetered, setTaskMetered ] = useState(itemOpened.metered)
  const [ taskPerformance, setTaskPerformance ] = useState(itemOpened.performance)
  const [ taskCrewNumber, setTaskCrewNumber ] = useState(itemOpened.crewNumber)
  const [ taskEstimatedDays, setTaskEstimatedDays ] = useState(itemOpened.estimatedDays)

  const costTypes: any = {
    'labor': t('COSTS_CREATOR:TypeLabor'),
    'material': t('COSTS_CREATOR:TypeMaterial'),
    'tool': t('COSTS_CREATOR:TypeTool'),
  }

  const itemCost = useMemo(() => {

    if (!itemOpened.resources?.length) return '0.00'

    let cost = 0

    itemOpened.resources.forEach((resource: any) => {

      let resCost = 0

      if (resource.type === 'labor') {

        resCost = Number(resource.crewNumber) * Number(resource.quantity) * Number(resource.unitPrice)

      } else if (resource.type === 'material') {

        resCost = (Number(resource.quantity) * Number(resource.unitPrice))
      } else if (resource.type === 'tool') {

        resCost = (Number(resource.quantity) * Number(resource.unitPrice))
      }

      cost += resCost
    })

    console.log(cost * taskMetered)

    return (cost * taskMetered).toFixed(2)
  }, [itemOpened.resources, taskMetered])

  const handleChangeField = async (field: string, value: any) => {

    switch (field) {
      case 'unit': {
        setTaskUnit(value)
        break
      }
      case 'currency': {
        setCurrency(value)
        break
      }
      case 'metered': {
        setTaskMetered(value)
        break
      }
      case 'performance': {
        setTaskPerformance(value)
        break
      }
      case 'crewNumber': {
        setTaskCrewNumber(value)
        break
      }
      case 'estimatedDays': {
        setTaskEstimatedDays(value)
        break
      }
    }

    setEditingItem(true)

    const [ error, data ] = await updateTaskField(itemOpened._id, field, value)

    setEditingItem(false)

    if (error) {
      return console.log(error)
    }

    await dispatch(updateTaskFieldAction(itemOpened._id, field, value))
  }

  const collapseKey = 'task-costs-collapse'

  const isCollapsed = useCallback(() => {
    const isCollapsed = localStorage.getItem(collapseKey)

    if (isCollapsed === 'true') {
      return true
    }

    return false
  }, [])

  useEffect(() => {

    const getEstimatedDays = () => {

      let estimatedDays = taskMetered / (taskPerformance * taskCrewNumber)

      if (parseInt(`${estimatedDays}`, 10) !== estimatedDays) {

        estimatedDays = (parseInt(`${estimatedDays}`, 10) + 1) || 0
      }

      setTaskEstimatedDays(estimatedDays)
    }

    if (taskMetered && taskPerformance && taskCrewNumber) {

      getEstimatedDays()
    } else {

      setTaskEstimatedDays(0)
    }
  }, [taskMetered, taskPerformance, taskCrewNumber])

  return (
    <ContentBlock
      title={t('PROGRAMMATION_MANAGER:SectionCostsTitle')}
      collapsable
      collapseKey={collapseKey}
      isCollapsed={isCollapsed()}
    >
      <FormGroup>
        <h5 className='group-title'>
          {t('COSTS_SECTION:InputEstimatedTimeLabel')}: {taskEstimatedDays ? taskEstimatedDays.toString() : '-'} {t('COSTS_SECTION:Days')}
        </h5>
      </FormGroup>
      <FormGroup as={Row}>
        <Col sm={4}>
          <FormLabel>
            {t('COSTS_SECTION:InputPerformanceLabel')}
          </FormLabel>
          <InputText
            value={taskPerformance ? taskPerformance.toString() : ''}
            onChange={(val: string) => handleChangeField('performance', Number(val))}
          />
        </Col>
        <Col sm={4}>
          <FormLabel>
            {t('COSTS_SECTION:InputCrewNumberLabel')}
          </FormLabel>
          <InputText
            value={taskCrewNumber ? taskCrewNumber.toString() : ''}
            onChange={(val: string) => handleChangeField('crewNumber', Number(val))}
          />
        </Col>
      </FormGroup>
      <div className='separator'></div>
      <FormGroup>
        <h5 className='group-title'>
          {t('COSTS_SECTION:ItemCost')}: {currency} {itemCost}
        </h5>
        <FormGroup as={Row}>
          <Col sm={4}>
            <FormLabel>
              {t('COSTS_SECTION:InputUnitLabel')}
            </FormLabel>
            <InputText
              value={taskUnit}
              onChange={(unit: string) => handleChangeField('unit', unit)}
            />
          </Col>
          <Col sm={4}>
            <FormLabel>
              {t('COSTS_SECTION:InputCurrencyLabel')}
            </FormLabel>
            <InputText
              value={currency}
              onChange={(currency: string) => handleChangeField('currency', currency)}
            />
          </Col>
          <Col sm={4}>
            <FormLabel>
              {t('COSTS_SECTION:InputQuantityLabel')}
            </FormLabel>
            <InputText
              value={taskMetered ? taskMetered.toString() : ''}
              onChange={(val: string) => handleChangeField('metered', Number(val))}
            />
          </Col>
        </FormGroup>
      </FormGroup>
      <div style={{ textAlign: 'right' }}>
        <CostCreator />
      </div>
      <div className='table-responsive'>
        <Table bordered className='table-resources'>
          <thead>
            <tr>
              <th style={{ width: 50 }}>
                {t('COSTS_SECTION:TypeColumn')}
              </th>
              <th>
                {t('COSTS_SECTION:DescriptionColumn')}
              </th>
              <th style={{ width: 65 }}>
                {t('COSTS_SECTION:UnitColumn')}
              </th>
              <th style={{ width: 80 }}>
                {t('COSTS_SECTION:CrewNumberColumn')}
              </th>
              <th style={{ width: 70 }}>
                {t('COSTS_SECTION:QuantityColumn')}
              </th>
              <th style={{ width: 70 }}>
                {t('COSTS_SECTION:UnitPriceColumn')}
              </th>
              <th style={{ width: 85, textAlign: 'right' }}>
                {t('COSTS_SECTION:PartialColumn')}
              </th>
              <th style={{ width: 60}}></th>
            </tr>
          </thead>
          <tbody>
            {itemOpened.resources?.map((resource: any) => (
              <CostItem key={resource._id} resource={resource} />
            ))}
          </tbody>
        </Table>
      </div>
    </ContentBlock>
  )
}

export default Costs
